import { Issuer, NewsItem } from "api/generated/cms";
import { CmsTag, mapCmsTags } from "./mapTags";
import { parseCmsId } from "./parseCmsId";

/**
 * The feed's filter value for news Adecla publishes itself — items the CMS
 * files under no issuer. Issuers filter by their CMS id, so a word cannot
 * collide with one.
 */
export const ADECLA_NEWS_SOURCE = "adecla";

const ADECLA_NAME = "Adecla";
const ADECLA_LOGO = `${process.env.PUBLIC_URL}/adecla.png`;

/** One news item, with the issuer that published it resolved. */
export interface CmsNewsArticle {
  /** Addresses the article in a list and in the article route. */
  key: string;
  /** The CMS id, or null for an article the CMS left unidentified — that one
   *  can be listed but has no page of its own. */
  articleId: number | null;
  /** The issuer's CMS id, or null for Adecla's own news. */
  issuerId: number | null;
  /** What the issuer filter matches on: the issuer's CMS id as a string, or
   *  `ADECLA_NEWS_SOURCE`. */
  source: string;
  issuerName: string;
  issuerLogo?: string;
  title: string;
  /** ISO `YYYY-MM-DD`, or whatever the editor typed — the CMS does not validate. */
  date: string;
  tags: CmsTag[];
  /** Teaser copy. `excerpt` is the field this replaces and is on its way out. */
  ingress?: string;
  /** Plain-text body. Most articles carry none; an article without it has
   *  nothing to show on its own page. */
  text?: string;
  image?: string;
  /** The issuer's own article page, when they published it themselves. */
  externalUrl?: string;
}

/** How long a title may run in a URL before it is cut. */
const MAX_SLUG_LENGTH = 80;

/**
 * Title → URL segment. Swedish vowels fold to their base letter rather than
 * being dropped, so "Räntor" stays readable as "rantor".
 */
const slugify = (title: string): string =>
  title
    .toLowerCase()
    .replace(/[åä]/g, "a")
    .replace(/ö/g, "o")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, MAX_SLUG_LENGTH)
    .replace(/-+$/, "");

/**
 * An article's URL segment: `<articleId>-<title slug>`.
 *
 * Article ids are global in the CMS, so the id alone identifies the article —
 * the slug is decoration, and retitling changes the URL without breaking the
 * old one.
 */
export const newsArticleKey = (articleId: number, title: string): string =>
  `${articleId}-${slugify(title)}`;

/** Reads the article id back out of a key, ignoring the slug. */
export const parseNewsArticleKey = (key: string): number | null => {
  const [id] = key.split("-");
  const articleId = Number(id);
  return id && Number.isFinite(articleId) ? articleId : null;
};

/** Whether a route key addresses this article, whatever slug it carries. */
export const matchesNewsArticle = (
  article: CmsNewsArticle,
  key: string
): boolean =>
  article.articleId !== null && parseNewsArticleKey(key) === article.articleId;

/** Later dates first; items the CMS left undated sink to the bottom. */
const byDateDescending = (a: CmsNewsArticle, b: CmsNewsArticle) =>
  (b.date || "").localeCompare(a.date || "");

/** Who published an item: its issuer when it names one, Adecla when it
 *  names none, and null when it names an issuer the CMS does not list. */
const publisherOf = (
  item: NewsItem,
  issuersByExternalId: Map<number, Issuer>
): Pick<
  CmsNewsArticle,
  "issuerId" | "source" | "issuerName" | "issuerLogo"
> | null => {
  const externalId = parseCmsId(item.externalId);
  if (externalId === null) {
    return {
      issuerId: null,
      source: ADECLA_NEWS_SOURCE,
      issuerName: ADECLA_NAME,
      issuerLogo: ADECLA_LOGO,
    };
  }
  const issuer = issuersByExternalId.get(externalId);
  const issuerId = issuer ? parseCmsId(issuer.id) : null;
  if (!issuer || issuerId === null) {
    return null;
  }
  return {
    issuerId,
    source: String(issuerId),
    issuerName: issuer.name ?? item.name ?? "",
    issuerLogo: issuer.logoImage ?? item.logoImage ?? undefined,
  };
};

const toArticle = (
  item: NewsItem,
  issuersByExternalId: Map<number, Issuer>
): CmsNewsArticle | null => {
  const title = item.title?.trim();
  const publisher = publisherOf(item, issuersByExternalId);
  if (!title || !publisher) {
    return null;
  }
  const articleId = parseCmsId(item.id);
  return {
    key:
      articleId === null
        ? `${publisher.source}-${slugify(title)}`
        : newsArticleKey(articleId, title),
    articleId,
    ...publisher,
    title,
    date: item.date ?? "",
    tags: mapCmsTags(item.tags),
    // `ingress` supersedes `excerpt`; both hold the same value today and
    // `excerpt` is being removed, so read it only as a fallback.
    ingress: item.ingress ?? item.excerpt ?? undefined,
    text: item.text?.trim() || undefined,
    // Same story for `imageUrl` over `image`.
    image: item.imageUrl ?? item.image ?? undefined,
    externalUrl: item.newsUrl ?? undefined,
  };
};

/** Every news item an issuer published, featured first as the CMS ordered it. */
export const issuerNewsItems = (issuer: Issuer): NewsItem[] => [
  ...(issuer.news?.featured ? [issuer.news.featured] : []),
  ...(issuer.news?.secondary ?? []),
];

/**
 * The news endpoint's items as a feed, newest first. Items name their issuer
 * by FA external id, so the CMS issuer list supplies the id, name and logo the
 * feed links and filters by.
 */
export const toNewsFeed = (
  items: NewsItem[],
  issuers: Issuer[]
): CmsNewsArticle[] => {
  const issuersByExternalId = new Map<number, Issuer>();
  issuers.forEach((issuer) => {
    const externalId = parseCmsId(issuer.externalId);
    if (externalId !== null) {
      issuersByExternalId.set(externalId, issuer);
    }
  });
  return items
    .flatMap((item) => {
      const article = toArticle(item, issuersByExternalId);
      return article ? [article] : [];
    })
    .sort(byDateDescending);
};

/** Distinct tag labels across the feed, alphabetical — the filter's options. */
export const newsFeedTagLabels = (articles: CmsNewsArticle[]): string[] =>
  Array.from(
    new Set(articles.flatMap((article) => article.tags.map((tag) => tag.label)))
  ).sort((a, b) => a.localeCompare(b, "sv"));
